import mapboxgl from 'mapbox-gl';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import type {
  MapHoverState,
  MapSelections,
  MapStateObservables,
} from '../models/map-observables.models';
import type { MapEventHandler } from '../map-event-handler';
import {
  MapLayerIds,
  MAP_THREEJS_LAYER_IDS,
} from '../helpers/map-config.constants';
import {
  canvasColors,
  CLICK_ANYWHERE_OTHER_BUILDINGS_OPACITY,
  getLineWidthZoomScale,
  mapRenderingConstants,
  BUILDING_SELECTION_OUTLINE_WIDTH_PX,
  BUILDING_SELECTION_HALO_WIDTH_PX,
} from '../map.constants';
import {
  areMapLayersPresent,
  setLayerVisibilityIfChanged,
  stackLayersBefore,
} from '../helpers/map-layer-order.helpers';
import {
  MAP_THREEJS_CUSTOM_LAYER_IDS,
  MAP_THREEJS_LAYER_ORDER_STACK,
} from '../helpers/map-threejs-layer-order.helpers';
import { isClickAnywhereDrawMode } from '@shared/helpers/design/storey.helpers';
import type { Building } from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { Coordinate } from '@shared/models/geography.model';
import type { Point2D } from '@shared/models/geometry.model';
import { hexToRgbArray } from '@shared/helpers/color.helpers';
import { getMapStoreyPointsFromStoreys } from '../helpers/map-features.helpers';
import type { MapStoreyPointFeature } from '../models/map-feature.models';
import {
  applyClipSpaceOffsetToMatrix,
  applyFloatingOriginMatrix,
  applyLocalAxisRotationToMatrix,
  projectLocalPointToScreen,
  unprojectScreenPointToLocalRay,
} from './floating-origin.helpers';
import type { BuildingVolumeRay } from '../helpers/building-pick.helpers';
import { initWireframeGl } from './gl-wireframe.helpers';
import { initExtrusionGl } from './gl-extrusion.helpers';
import { initPointsGl, type PointsGlResources } from './gl-points.helpers';
import {
  disposeSilhouetteGl,
  drawSilhouette,
  initSilhouetteGl,
  resizeSilhouetteTarget,
  type SilhouetteGlResources,
} from './gl-silhouette.helpers';
import {
  disposeFacadeSkyGl,
  drawFacadeSky,
  initFacadeSkyGl,
  projectFacadeLevelNdcY,
  projectGroundLevelNdcY,
  resolveFacadeHeightGridElevations,
  type FacadeSkyGlResources,
} from './gl-facade-sky.helpers';
import { buildBuildingMassVertices } from './building-mass-vertices.helpers';
import { getHoveredStoreyForOutline } from '../helpers/storey-selection.helpers';
import {
  defaultExtrusionRenderParams,
  resolveGlBlendFactor,
  type ExtrusionRenderParams,
} from './extrusion-render-params.helpers';
import { buildWireframeVertices } from './wireframe-vertices';
import { buildExtrusionVertices } from './extrusion-vertices.helpers';
import {
  concatExtrusionGeometries,
  concatWireframeGeometries,
} from './concat-geometry.helpers';
import { buildPointVertices } from './point-vertices.helpers';
import { createPointDragGuidesLayer } from './point-drag-guides-layer.helpers';
import {
  applyShapeTranslationPreview,
  resolvePolygonDragOrigin,
} from './translation-preview.helpers';
import { resolveExtrusionLightSettings } from './light.helpers';
import { shouldDrawStoreyFillsWithUnderlay } from './threejs-mode.helpers';
import type {
  ExtrusionGlResources,
  ExtrusionLayerState,
  GeometryPiece,
  StoreyRenderSlice,
  ThreeJsMode,
  WireframeGlResources,
  WireframeLayerState,
} from './types';

const LAYER_ID = MAP_THREEJS_LAYER_IDS.wireframe;
const EXTRUSION_LAYER_ID = MAP_THREEJS_LAYER_IDS.extrusion;
const POINTS_LAYER_ID = MAP_THREEJS_LAYER_IDS.points;
const SILHOUETTE_LAYER_ID = MAP_THREEJS_LAYER_IDS.silhouette;
const FACADE_SKY_LAYER_ID = MAP_THREEJS_LAYER_IDS.facadeSky;

/** Hover is a neutral affordance; blue stays reserved for the selection. */
const SILHOUETTE_HOVER_COLOR = '#000000';
const FACADE_PITCH_DEGREES = 90;

const resolveProjectionMatrix = ({
  map,
  originMercator,
  facadeTransitionProgress,
  facadeVerticalOffsetPixels,
  matrix,
}: {
  map: mapboxgl.Map;
  originMercator: mapboxgl.MercatorCoordinate;
  facadeTransitionProgress: number;
  facadeVerticalOffsetPixels: number;
  matrix: readonly number[];
}): Float32Array => {
  const floatingOriginMatrix = applyFloatingOriginMatrix(
    matrix,
    originMercator,
  );
  if (facadeTransitionProgress <= 0) return floatingOriginMatrix;

  const bearingRadians = (map.getBearing() * Math.PI) / 180;
  const pitchDeltaRadians =
    ((FACADE_PITCH_DEGREES - map.getPitch()) *
      facadeTransitionProgress *
      Math.PI) /
    180;
  const center = mapboxgl.MercatorCoordinate.fromLngLat(map.getCenter(), 0);
  const facadeMatrix = applyLocalAxisRotationToMatrix(floatingOriginMatrix, {
    axis: [Math.cos(bearingRadians), Math.sin(bearingRadians), 0],
    center: [
      center.x - originMercator.x,
      center.y - originMercator.y,
      center.z - originMercator.z,
    ],
    radians: pitchDeltaRadians,
  });
  const viewportHeight = map.getCanvas().clientHeight;
  if (!(viewportHeight > 0) || facadeVerticalOffsetPixels === 0) {
    return facadeMatrix;
  }
  return applyClipSpaceOffsetToMatrix(facadeMatrix, {
    yNdc:
      (facadeVerticalOffsetPixels * facadeTransitionProgress * 2) /
      viewportHeight,
  });
};

const EMPTY_WIREFRAME: WireframeLayerState = {
  vertices: new Float32Array(),
  pointCount: 0,
  storeySlices: [],
  pieceSlices: [],
  cutoutVerticalSlices: [],
  overlayVertices: new Float32Array(),
  overlayPointCount: 0,
};

const EMPTY_EXTRUSION: ExtrusionLayerState = {
  vertices: new Float32Array(),
  vertexCount: 0,
  storeySlices: [],
  wallVertices: new Float32Array(),
  wallVertexCount: 0,
  roofVertices: new Float32Array(),
  roofVertexCount: 0,
};

/**
 * MapThreeJs renders a 3D wireframe + extrusion overlay above the Mapbox style, based on the current design storeys.
 *
 * This intentionally uses Mapbox's custom layer API (WebGL) instead of bringing in {@link https://threejs.org three.js},
 * because this repo currently does not ship the `three` / `threebox-plugin` dependencies.
 */
export class MapThreeJs {
  private readonly subscription = new Subscription();
  private detachStyleListener: (() => void) | undefined;
  private detachZoomListener: (() => void) | undefined;
  private readonly readonly$ = new BehaviorSubject<boolean>(false);
  private readonly zoom$ = new BehaviorSubject<number>(0);
  private readonly mode$: BehaviorSubject<ThreeJsMode>;
  /**
   * Soft grey for storeys below the selection (2D + PDF contrast). Off in 3D.
   */
  private readonly softenGuidesBelowSelected$ = new BehaviorSubject(false);
  /**
   * When set, solid fills for storeys below this elevation (meters) are skipped
   * so an elevated PDF underlay stays dominant. Wireframe outlines still draw.
   */
  private hideFillsBelowElevationMeters: number | undefined;
  readonly renderParams$ = new BehaviorSubject<ExtrusionRenderParams>(
    defaultExtrusionRenderParams,
  );
  private isReadonly = false;
  /** Skip drawing buildings while the PDF scale line is being placed. */
  private measureIsolationActive = false;
  /** Exact facade projection used after the mouse gesture passes Mapbox's 85° cap. */
  private facadeModeActive = false;
  /** Animated blend from Mapbox's 85° projection to the exact 90° facade. */
  private facadeTransitionProgress = 0;
  /** Screen-space elevation offset accumulated by facade-mode vertical panning. */
  private facadeVerticalOffsetPixels = 0;

  private latest: WireframeLayerState = {
    vertices: new Float32Array(),
    pointCount: 0,
    storeySlices: [],
    pieceSlices: [],
    cutoutVerticalSlices: [],
    overlayVertices: new Float32Array(),
    overlayPointCount: 0,
  };
  private latestExtrusion: ExtrusionLayerState = {
    vertices: new Float32Array(),
    vertexCount: 0,
    storeySlices: [],
    wallVertices: new Float32Array(),
    wallVertexCount: 0,
    roofVertices: new Float32Array(),
    roofVertexCount: 0,
  };
  private latestPoints: { vertices: Float32Array; vertexCount: number } = {
    vertices: new Float32Array(),
    vertexCount: 0,
  };

  private originMercator: mapboxgl.MercatorCoordinate;
  private bufferDirty = false;
  private extrusionBufferDirty = false;
  private pointsBufferDirty = false;

  private silhouetteMass: Float32Array<ArrayBufferLike> = new Float32Array();
  private hoveredStoreyOutlineActive = false;
  private hoveredSilhouetteMass: Float32Array<ArrayBufferLike> =
    new Float32Array();
  private silhouetteBufferDirty = false;
  private silhouetteGlResources: SilhouetteGlResources | undefined;
  private facadeSkyGlResources: FacadeSkyGlResources | undefined;
  private glResources: WireframeGlResources | undefined;
  private extrusionGlResources: ExtrusionGlResources | undefined;
  private pointsGlResources: PointsGlResources | undefined;
  /**
   * Sibling 3D geometry rebuilt only when other buildings / view params change.
   * Shape-drag frames reuse it so translating one storey does not rebuild the site.
   */
  private siblingGeometryCache:
    | {
        otherBuildings: Building[];
        selectedStoreyId: string | undefined;
        storeyCount: number;
        mode: ThreeJsMode;
        opacity: number;
        lng: number;
        lat: number;
        renderParams: ExtrusionRenderParams;
        wireframe: WireframeLayerState;
        extrusion: ExtrusionLayerState;
      }
    | undefined;

  private readonly layer: mapboxgl.CustomLayerInterface;
  private readonly facadeSkyLayer: mapboxgl.CustomLayerInterface;
  private readonly pointDragGuidesLayer: mapboxgl.CustomLayerInterface;
  private readonly extrusionLayer: mapboxgl.CustomLayerInterface;
  private readonly pointsLayer: mapboxgl.CustomLayerInterface;
  private readonly silhouetteLayer: mapboxgl.CustomLayerInterface;

  /**
   * @param map Active {@link mapboxgl.Map}
   * @param state Shared {@link MapStateObservables}
   * @param events {@link MapEventHandler} used to listen for live interaction previews (e.g. translations)
   * @param options Configuration for colors and rendering mode
   */
  constructor(
    private readonly map: mapboxgl.Map,
    private readonly state: MapStateObservables,
    private readonly events: MapEventHandler,
    private readonly options?: {
      /** Render only the selected storey (default), or all storeys. */
      mode?: ThreeJsMode;
    },
  ) {
    // Keep a stable "floating origin" to avoid meter-scale Float32 jitter in Mercator coordinates.
    this.originMercator = mapboxgl.MercatorCoordinate.fromLngLat(
      state.location$.getValue(),
      0,
    );
    this.mode$ = new BehaviorSubject<ThreeJsMode>(
      options?.mode ?? 'all-storeys',
    );

    this.pointDragGuidesLayer = createPointDragGuidesLayer({
      getPointMove: () =>
        this.measureIsolationActive
          ? undefined
          : this.events.getPointSnapGuidePreview(),
      getLocation: () => this.state.location$.getValue(),
      getProjection: (matrix) =>
        resolveProjectionMatrix({
          map: this.map,
          originMercator: this.originMercator,
          facadeTransitionProgress: this.facadeTransitionProgress,
          facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
          matrix,
        }),
    });

    this.facadeSkyLayer = {
      id: FACADE_SKY_LAYER_ID,
      type: 'custom',
      renderingMode: '2d',
      onAdd: (_mapInstance, gl) => {
        this.facadeSkyGlResources = initFacadeSkyGl(gl);
      },
      render: (gl, matrix) => {
        if (!this.facadeModeActive || this.measureIsolationActive) return;
        const resources = this.facadeSkyGlResources;
        if (!resources) return;

        const projection = resolveProjectionMatrix({
          map: this.map,
          originMercator: this.originMercator,
          facadeTransitionProgress: this.facadeTransitionProgress,
          facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
          matrix,
        });
        const mapCenter = this.map.getCenter();
        const center = mapboxgl.MercatorCoordinate.fromLngLat(mapCenter, 0);
        const groundNdcY = projectGroundLevelNdcY(projection, [
          center.x - this.originMercator.x,
          center.y - this.originMercator.y,
          center.z - this.originMercator.z,
        ]);
        const buildingStoreys = [
          this.state.storeys$.getValue(),
          ...this.state.otherBuildings$
            .getValue()
            .map((building) => building.storeys),
        ];
        const gridNdcYs = resolveFacadeHeightGridElevations(buildingStoreys)
          .map((elevation) => {
            const level = mapboxgl.MercatorCoordinate.fromLngLat(
              mapCenter,
              elevation,
            );
            return projectFacadeLevelNdcY(projection, [
              level.x - this.originMercator.x,
              level.y - this.originMercator.y,
              level.z - this.originMercator.z,
            ]);
          })
          .filter((y): y is number => y !== undefined && y >= -1 && y <= 1);
        drawFacadeSky({ gl, resources, groundNdcY, gridNdcYs });
      },
      onRemove: (_mapInstance, gl) => {
        const resources = this.facadeSkyGlResources;
        if (resources) disposeFacadeSkyGl(gl, resources);
        this.facadeSkyGlResources = undefined;
      },
    };

    this.extrusionLayer = {
      id: EXTRUSION_LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd: (_mapInstance, gl) => {
        this.extrusionGlResources = initExtrusionGl(gl);
        this.syncExtrusionGlBuffer(gl);
      },
      render: (gl, matrix) => {
        if (this.measureIsolationActive) return;
        const wasDepthTest = gl.isEnabled(gl.DEPTH_TEST);
        const wasBlend = gl.isEnabled(gl.BLEND);
        const wasCullFace = gl.isEnabled(gl.CULL_FACE);

        const resources = this.extrusionGlResources;
        if (!resources) return;

        if (this.extrusionBufferDirty) {
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
          gl.bufferData(
            gl.ARRAY_BUFFER,
            this.latestExtrusion.vertices,
            gl.DYNAMIC_DRAW,
          );
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.wallBuffer);
          gl.bufferData(
            gl.ARRAY_BUFFER,
            this.latestExtrusion.wallVertices,
            gl.DYNAMIC_DRAW,
          );
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.roofBuffer);
          gl.bufferData(
            gl.ARRAY_BUFFER,
            this.latestExtrusion.roofVertices,
            gl.DYNAMIC_DRAW,
          );
          this.extrusionBufferDirty = false;
        }

        gl.useProgram(resources.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);

        // Layout: pos(3), normal(3), color(4) = 10 floats
        const stride = 10 * 4;
        gl.enableVertexAttribArray(resources.aPosition);
        gl.vertexAttribPointer(
          resources.aPosition,
          3,
          gl.FLOAT,
          false,
          stride,
          0,
        );

        gl.enableVertexAttribArray(resources.aNormal);
        gl.vertexAttribPointer(
          resources.aNormal,
          3,
          gl.FLOAT,
          false,
          stride,
          3 * 4,
        );

        gl.enableVertexAttribArray(resources.aColor);
        gl.vertexAttribPointer(
          resources.aColor,
          4,
          gl.FLOAT,
          false,
          stride,
          6 * 4,
        );

        gl.uniformMatrix4fv(
          resources.uMatrix,
          false,
          resolveProjectionMatrix({
            map: this.map,
            originMercator: this.originMercator,
            facadeTransitionProgress: this.facadeTransitionProgress,
            facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
            matrix,
          }),
        );

        const params = this.renderParams$.getValue();

        const light = params.useLightOverride
          ? {
              dir: [params.lightDirX, params.lightDirY, params.lightDirZ] as [
                number,
                number,
                number,
              ],
              ambient: params.lightAmbient,
              strength: params.lightStrength,
            }
          : resolveExtrusionLightSettings(this.map);
        gl.uniform3f(
          resources.uLightDir,
          light.dir[0],
          light.dir[1],
          light.dir[2],
        );
        gl.uniform1f(resources.uAmbient, light.ambient);
        gl.uniform1f(resources.uStrength, light.strength);

        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);

        gl.enable(gl.BLEND);
        gl.blendFunc(
          resolveGlBlendFactor(gl, params.blendSrc),
          resolveGlBlendFactor(gl, params.blendDst),
        );

        const bindAttribs = (buf: WebGLBuffer) => {
          gl.bindBuffer(gl.ARRAY_BUFFER, buf);
          gl.vertexAttribPointer(
            resources.aPosition,
            3,
            gl.FLOAT,
            false,
            stride,
            0,
          );
          gl.vertexAttribPointer(
            resources.aNormal,
            3,
            gl.FLOAT,
            false,
            stride,
            3 * 4,
          );
          gl.vertexAttribPointer(
            resources.aColor,
            4,
            gl.FLOAT,
            false,
            stride,
            6 * 4,
          );
        };

        // Two-level sort: first between storeys, then within each storey.
        const slices = this.latestExtrusion.storeySlices;
        if (slices.length > 0) {
          const mapPitch = this.map.getPitch();
          const pitchRad =
            ((mapPitch +
              (FACADE_PITCH_DEGREES - mapPitch) *
                this.facadeTransitionProgress) *
              Math.PI) /
            180;
          const bearingRad = (this.map.getBearing() * Math.PI) / 180;

          // Scene-to-camera direction in Mercator space (Y increases south).
          // At bearing=0: camera is south (+Y) and above (+Z).
          // At bearing=90 (east up): camera is west (-X) and above (+Z).
          const camDirX = -Math.sin(bearingRad) * Math.sin(pitchRad);
          const camDirY = Math.cos(bearingRad) * Math.sin(pitchRad);
          const camDirZ = Math.cos(pitchRad);

          const dot3 = (c: [number, number, number]) =>
            c[0] * camDirX + c[1] * camDirY + c[2] * camDirZ;

          // Always sort strictly by elevation (centroid Z, lower first → drawn first → behind).
          // Camera-based depth sort is not reliable for inter-storey ordering since two
          // storeys at different elevations could invert under high-pitch camera angles.
          const sortedStoreys = [...slices].sort(
            (a, b) => a.centroid[2] - b.centroid[2],
          );

          const byDepth = (a: GeometryPiece, b: GeometryPiece) =>
            dot3(a.centroid) - dot3(b.centroid);

          const drawWallPiece = (piece: GeometryPiece) => {
            bindAttribs(resources.wallBuffer);
            gl.depthMask(params.depthMaskWalls);
            gl.enable(gl.CULL_FACE);
            gl.cullFace(gl.FRONT);
            gl.drawArrays(gl.TRIANGLES, piece.first, piece.count);
            gl.cullFace(gl.BACK);
            gl.drawArrays(gl.TRIANGLES, piece.first, piece.count);
            gl.disable(gl.CULL_FACE);
            gl.depthMask(true);
          };

          const drawRoofPiece = (piece: GeometryPiece) => {
            bindAttribs(resources.roofBuffer);
            gl.depthMask(params.depthMaskRoofs);
            gl.enable(gl.CULL_FACE);
            gl.cullFace(gl.FRONT);
            gl.drawArrays(gl.TRIANGLES, piece.first, piece.count);
            gl.cullFace(gl.BACK);
            gl.drawArrays(gl.TRIANGLES, piece.first, piece.count);
            gl.disable(gl.CULL_FACE);
            gl.depthMask(true);
          };

          // Ensure wireframe buffer is up-to-date before interleaved draws.
          // The wireframe layer may not have run yet this frame, so we sync here
          // if dirty to avoid using stale vertex positions after edits.
          const wfResForSync = this.glResources;
          if (wfResForSync && this.bufferDirty) {
            gl.bindBuffer(gl.ARRAY_BUFFER, wfResForSync.buffer);
            gl.bufferData(
              gl.ARRAY_BUFFER,
              this.latest.vertices,
              gl.DYNAMIC_DRAW,
            );
            gl.bindBuffer(gl.ARRAY_BUFFER, wfResForSync.overlayBuffer);
            gl.bufferData(
              gl.ARRAY_BUFFER,
              this.latest.overlayVertices,
              gl.DYNAMIC_DRAW,
            );
            this.bufferDirty = false;
          }

          // Build a storeyId → wireframe slice lookup (mezzanine / fallback).
          const wireSliceByStorey = new Map(
            this.latest.storeySlices.map((s) => [s.storeyId, s]),
          );

          // Build per-edge lookup: "storeyId:shapeIndex:edgeIndex" → WireframePieceSlice.
          const wireSliceByPiece = new Map(
            this.latest.pieceSlices.map((s) => [
              `${s.storeyId}:${s.shapeIndex}:${s.edgeIndex}`,
              s,
            ]),
          );

          // Shared wireframe GL setup + draw. Binds attribs, sets uniforms, draws
          // [first, first+count) from the main wireframe buffer, then restores extrusion state.
          const drawWireframeRange = (first: number, count: number) => {
            const wfRes = this.glResources;
            if (!wfRes) return;

            gl.useProgram(wfRes.program);
            gl.bindBuffer(gl.ARRAY_BUFFER, wfRes.buffer);

            const wfStride = 21 * 4;
            gl.enableVertexAttribArray(wfRes.aPositionHigh);
            gl.vertexAttribPointer(
              wfRes.aPositionHigh,
              3,
              gl.FLOAT,
              false,
              wfStride,
              0,
            );
            gl.enableVertexAttribArray(wfRes.aPositionLow);
            gl.vertexAttribPointer(
              wfRes.aPositionLow,
              3,
              gl.FLOAT,
              false,
              wfStride,
              3 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aOtherHigh);
            gl.vertexAttribPointer(
              wfRes.aOtherHigh,
              3,
              gl.FLOAT,
              false,
              wfStride,
              6 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aOtherLow);
            gl.vertexAttribPointer(
              wfRes.aOtherLow,
              3,
              gl.FLOAT,
              false,
              wfStride,
              9 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aSide);
            gl.vertexAttribPointer(
              wfRes.aSide,
              1,
              gl.FLOAT,
              false,
              wfStride,
              12 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aAlong);
            gl.vertexAttribPointer(
              wfRes.aAlong,
              1,
              gl.FLOAT,
              false,
              wfStride,
              13 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aColor);
            gl.vertexAttribPointer(
              wfRes.aColor,
              4,
              gl.FLOAT,
              false,
              wfStride,
              14 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aData);
            gl.vertexAttribPointer(
              wfRes.aData,
              2,
              gl.FLOAT,
              false,
              wfStride,
              18 * 4,
            );
            gl.enableVertexAttribArray(wfRes.aWidth);
            gl.vertexAttribPointer(
              wfRes.aWidth,
              1,
              gl.FLOAT,
              false,
              wfStride,
              20 * 4,
            );

            gl.uniformMatrix4fv(
              wfRes.uMatrix,
              false,
              resolveProjectionMatrix({
                map: this.map,
                originMercator: this.originMercator,
                facadeTransitionProgress: this.facadeTransitionProgress,
                facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
                matrix,
              }),
            );
            gl.uniform2f(
              wfRes.uDashParams,
              mapRenderingConstants.dashSizePx,
              mapRenderingConstants.gapSizePx,
            );
            gl.uniform1f(
              wfRes.uPixelsPerMercator,
              Math.pow(2, this.map.getZoom()) * 512,
            );
            const canvas = this.map.getCanvas();
            gl.uniform2f(wfRes.uResolution, canvas.width, canvas.height);
            const dpr =
              typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
            gl.uniform1f(
              wfRes.uLineWidth,
              mapRenderingConstants.lineWidthPx *
                dpr *
                getLineWidthZoomScale(this.map.getZoom()),
            );

            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            // Ignore depth so outlines of other storeys stay visible as guides.
            gl.disable(gl.DEPTH_TEST);
            gl.drawArrays(gl.TRIANGLES, first, count);
            gl.enable(gl.DEPTH_TEST);

            gl.useProgram(resources.program);
            gl.blendFunc(
              resolveGlBlendFactor(gl, params.blendSrc),
              resolveGlBlendFactor(gl, params.blendDst),
            );
          };

          // Draw wireframe for a single fill piece (wall edge) immediately after its fill.
          const drawWireframePiece = (
            piece: GeometryPiece,
            storeyId: string,
          ) => {
            if (piece.shapeIndex === undefined || piece.edgeIndex === undefined)
              return;
            const wfSlice = wireSliceByPiece.get(
              `${storeyId}:${piece.shapeIndex}:${piece.edgeIndex}`,
            );
            if (!wfSlice) return;
            const hasRoofLine =
              wfSlice.roofLineCount != null &&
              wfSlice.roofLineCount > 0 &&
              wfSlice.roofLineFirst != null;
            if (wfSlice.count <= 0 && !hasRoofLine) return;
            if (wfSlice.count > 0) {
              drawWireframeRange(wfSlice.first, wfSlice.count);
            }
            // Roof outline lives in a later buffer range; when roof fill is off, draw it here.
            if (hasRoofLine && !params.showRoofs) {
              const rf = wfSlice.roofLineFirst;
              const rc = wfSlice.roofLineCount;
              if (rf != null && rc != null) drawWireframeRange(rf, rc);
            }
          };

          /** Roof-elevation horizontals: must render after transparent roof fill. */
          const drawDeferredRoofOutlines = (storeyId: string) => {
            for (const s of this.latest.pieceSlices) {
              if (s.storeyId !== storeyId) continue;
              const rf = s.roofLineFirst;
              const rc = s.roofLineCount;
              if (rf == null || rc == null || rc <= 0) continue;
              drawWireframeRange(rf, rc);
            }
          };

          // Draw all wireframe lines for a storey in one call (used for mezzanines).
          const drawWireframeForStorey = (storeyId: string) => {
            const wfSlice = wireSliceByStorey.get(storeyId);
            if (!wfSlice || wfSlice.count <= 0) return;
            drawWireframeRange(wfSlice.first, wfSlice.count);
          };

          // Mezzanines are rendered inside their parent storey's pass (after parent
          // floor, before parent roof). Build a lookup of child slices by parent ID.
          const mezzaninesByParent = new Map<string, StoreyRenderSlice[]>();
          for (const slice of slices) {
            if (slice.isMezzanine && slice.parentStoreyId) {
              const arr = mezzaninesByParent.get(slice.parentStoreyId) ?? [];
              arr.push(slice);
              mezzaninesByParent.set(slice.parentStoreyId, arr);
            }
          }

          // Top-level pass: only non-mezzanine slices, or mezzanines whose parent
          // is not present in this render (e.g. selected-storey mode).
          const parentIds = new Set(slices.map((s) => s.storeyId));
          const topLevelStoreys = sortedStoreys.filter(
            (s) =>
              !s.isMezzanine ||
              !s.parentStoreyId ||
              !parentIds.has(s.parentStoreyId),
          );

          const drawFloorPiece = (piece: GeometryPiece) => {
            bindAttribs(resources.buffer);
            gl.depthMask(params.depthMaskFloors);
            gl.drawArrays(gl.TRIANGLES, piece.first, piece.count);
            gl.depthMask(true);
          };

          const storeysForFillGate = this.state.storeys$.getValue();
          const hideFillsBelow = this.hideFillsBelowElevationMeters;
          const drawsFills = (storeyId: string) =>
            shouldDrawStoreyFillsWithUnderlay(
              storeysForFillGate,
              storeyId,
              hideFillsBelow,
            );

          for (const slice of topLevelStoreys) {
            const backWalls: GeometryPiece[] = [];
            const frontWalls: GeometryPiece[] = [];
            const floors: GeometryPiece[] = [];
            const roofs: GeometryPiece[] = [];
            const drawFills = drawsFills(slice.storeyId);

            for (const piece of slice.pieces) {
              if (piece.buffer === 'floor') {
                floors.push(piece);
              } else if (piece.buffer === 'roof') {
                roofs.push(piece);
              } else if (piece.normal) {
                const facing =
                  piece.normal[0] * camDirX + piece.normal[1] * camDirY;
                if (facing > 0) {
                  frontWalls.push(piece);
                } else {
                  backWalls.push(piece);
                }
              }
            }

            backWalls.sort(byDepth);
            frontWalls.sort(byDepth);

            // Render order per element:
            //   backWall#i fill → backWall#i wireframe (no roof-elevation horizontals yet)
            //   floor fill (no per-floor wireframe; outlines are owned by wall edges)
            //   mezzanine floor fill → mezzanine storey wireframe
            //   frontWall#i fill → frontWall#i wireframe
            //   cutout verticals (after all wall quads so corners are not occluded)
            //   roof fill → deferred roof-outline horizontals (black on top of transparent roof)
            for (const w of backWalls) {
              if (params.showWalls && drawFills) drawWallPiece(w);
              drawWireframePiece(w, slice.storeyId);
            }

            if (drawFills) {
              for (const f of floors) drawFloorPiece(f);
            }

            // Mezzanines: after parent floor, before parent roof.
            const childMezzanines =
              mezzaninesByParent.get(slice.storeyId) ?? [];
            for (const mezz of childMezzanines) {
              if (drawsFills(mezz.storeyId)) {
                for (const mp of mezz.pieces) {
                  if (mp.buffer === 'floor') drawFloorPiece(mp);
                }
              }
              drawWireframeForStorey(mezz.storeyId);
            }

            for (const w of frontWalls) {
              if (params.showWalls && drawFills) drawWallPiece(w);
              drawWireframePiece(w, slice.storeyId);
            }

            for (const cv of this.latest.cutoutVerticalSlices) {
              if (cv.storeyId !== slice.storeyId || cv.count <= 0) continue;
              drawWireframeRange(cv.first, cv.count);
            }

            if (params.showRoofs) {
              if (drawFills) {
                for (const r of roofs) drawRoofPiece(r);
              }
              drawDeferredRoofOutlines(slice.storeyId);
            }
          }
        } else {
          bindAttribs(resources.buffer);
          gl.depthMask(params.depthMaskFloors);
          gl.drawArrays(gl.TRIANGLES, 0, this.latestExtrusion.vertexCount);
          gl.depthMask(true);
        }

        // Henrik: reset GL state so Mapbox layers (points) render predictably after us.
        if (!wasDepthTest) gl.disable(gl.DEPTH_TEST);
        if (!wasBlend) gl.disable(gl.BLEND);
        if (wasCullFace) gl.enable(gl.CULL_FACE);
      },
      onRemove: (_mapInstance, gl) => {
        const resources = this.extrusionGlResources;
        if (!resources) return;

        gl.deleteBuffer(resources.buffer);
        gl.deleteBuffer(resources.wallBuffer);
        gl.deleteBuffer(resources.roofBuffer);
        gl.deleteProgram(resources.program);
        this.extrusionGlResources = undefined;
      },
    };

    this.pointsLayer = {
      id: POINTS_LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd: (_mapInstance, gl) => {
        this.pointsGlResources = initPointsGl(gl);
        this.syncPointsGlBuffer(gl);
      },
      render: (gl, matrix) => {
        if (this.measureIsolationActive || this.facadeModeActive) return;
        const wasDepthTest = gl.isEnabled(gl.DEPTH_TEST);
        const wasBlend = gl.isEnabled(gl.BLEND);
        const wasCullFace = gl.isEnabled(gl.CULL_FACE);

        const resources = this.pointsGlResources;
        if (!resources) return;

        if (this.pointsBufferDirty) {
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
          gl.bufferData(
            gl.ARRAY_BUFFER,
            this.latestPoints.vertices,
            gl.DYNAMIC_DRAW,
          );
          this.pointsBufferDirty = false;
        }

        gl.useProgram(resources.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);

        // Layout: pos(3), corner(2), fill(3), stroke(3), opacity(1), size(1), strokeScale(1) = 14 floats
        const stride = 14 * 4;

        gl.enableVertexAttribArray(resources.aPosition);
        gl.vertexAttribPointer(
          resources.aPosition,
          3,
          gl.FLOAT,
          false,
          stride,
          0,
        );

        gl.enableVertexAttribArray(resources.aCorner);
        gl.vertexAttribPointer(
          resources.aCorner,
          2,
          gl.FLOAT,
          false,
          stride,
          3 * 4,
        );

        gl.enableVertexAttribArray(resources.aFill);
        gl.vertexAttribPointer(
          resources.aFill,
          3,
          gl.FLOAT,
          false,
          stride,
          5 * 4,
        );

        gl.enableVertexAttribArray(resources.aStroke);
        gl.vertexAttribPointer(
          resources.aStroke,
          3,
          gl.FLOAT,
          false,
          stride,
          8 * 4,
        );

        gl.enableVertexAttribArray(resources.aOpacity);
        gl.vertexAttribPointer(
          resources.aOpacity,
          1,
          gl.FLOAT,
          false,
          stride,
          11 * 4,
        );

        gl.enableVertexAttribArray(resources.aSize);
        gl.vertexAttribPointer(
          resources.aSize,
          1,
          gl.FLOAT,
          false,
          stride,
          12 * 4,
        );

        gl.enableVertexAttribArray(resources.aStrokeScale);
        gl.vertexAttribPointer(
          resources.aStrokeScale,
          1,
          gl.FLOAT,
          false,
          stride,
          13 * 4,
        );

        gl.uniformMatrix4fv(
          resources.uMatrix,
          false,
          resolveProjectionMatrix({
            map: this.map,
            originMercator: this.originMercator,
            facadeTransitionProgress: this.facadeTransitionProgress,
            facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
            matrix,
          }),
        );

        const canvas = this.map.getCanvas();
        gl.uniform2f(resources.uResolution, canvas.width, canvas.height);
        const dpr =
          typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
        gl.uniform1f(resources.uPixelRatio, dpr);
        gl.uniform1f(
          resources.uStrokeWidth,
          mapRenderingConstants.lineWidthPx * dpr,
        );

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Overlay points above extrusions + lines.
        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.CULL_FACE);

        gl.drawArrays(gl.TRIANGLES, 0, this.latestPoints.vertexCount);

        if (wasDepthTest) gl.enable(gl.DEPTH_TEST);
        if (!wasBlend) gl.disable(gl.BLEND);
        if (wasCullFace) gl.enable(gl.CULL_FACE);
      },
      onRemove: (_mapInstance, gl) => {
        const resources = this.pointsGlResources;
        if (!resources) return;

        gl.deleteBuffer(resources.buffer);
        gl.deleteProgram(resources.program);
        this.pointsGlResources = undefined;
      },
    };

    this.silhouetteLayer = {
      id: SILHOUETTE_LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd: (_mapInstance, gl) => {
        this.silhouetteGlResources = initSilhouetteGl(gl);
        this.syncSilhouetteGlBuffer(gl);
      },
      render: (gl, matrix) => {
        if (this.measureIsolationActive) return;
        const resources = this.silhouetteGlResources;
        if (!resources) return;
        const selectedCount = this.silhouetteMass.length / 3;
        const hoveredCount = this.hoveredSilhouetteMass.length / 3;
        if (selectedCount <= 0 && hoveredCount <= 0) return;

        // The canvas can change size between frames (panel resize, DPI).
        const currentResources = resizeSilhouetteTarget(gl, resources);
        this.silhouetteGlResources = currentResources;
        if (this.silhouetteBufferDirty) this.syncSilhouetteGlBuffer(gl);

        const pixelRatio = window.devicePixelRatio || 1;
        const matrixWithOrigin = resolveProjectionMatrix({
          map: this.map,
          originMercator: this.originMercator,
          facadeTransitionProgress: this.facadeTransitionProgress,
          facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
          matrix,
        });
        const withHalo = (
          color: string,
        ): { color: [number, number, number, number]; widthPx: number }[] => [
          {
            color: [...hexToRgbArray(canvasColors.lineSelectedHalo), 1],
            widthPx: BUILDING_SELECTION_HALO_WIDTH_PX * pixelRatio,
          },
          {
            color: [...hexToRgbArray(color), 1],
            widthPx: BUILDING_SELECTION_OUTLINE_WIDTH_PX * pixelRatio,
          },
        ];

        const drawSelected = () =>
          drawSilhouette({
            gl,
            resources: currentResources,
            matrix: matrixWithOrigin,
            massBuffer: currentResources.selectedMassBuffer,
            vertexCount: selectedCount,
            outlines: withHalo(canvasColors.lineSelected),
          });
        // A hovered floor sits above its selected building; sibling-building hover stays below.
        if (this.hoveredStoreyOutlineActive) drawSelected();
        drawSilhouette({
          gl,
          resources: currentResources,
          matrix: matrixWithOrigin,
          massBuffer: currentResources.hoveredMassBuffer,
          vertexCount: hoveredCount,
          outlines: withHalo(SILHOUETTE_HOVER_COLOR),
        });
        if (!this.hoveredStoreyOutlineActive) drawSelected();
      },
      onRemove: (_mapInstance, gl) => {
        const resources = this.silhouetteGlResources;
        if (resources) disposeSilhouetteGl(gl, resources);
        this.silhouetteGlResources = undefined;
      },
    };

    this.layer = {
      id: LAYER_ID,
      type: 'custom',
      renderingMode: '3d',
      onAdd: (_mapInstance, gl) => {
        // Mapbox calls onAdd once per style-context; we lazily (re)build GL resources here.
        this.glResources = initWireframeGl(gl);
        this.syncGlBuffer(gl);
      },
      render: (gl, matrix) => {
        if (this.measureIsolationActive) return;
        const wasDepthTest = gl.isEnabled(gl.DEPTH_TEST);
        const wasBlend = gl.isEnabled(gl.BLEND);
        const wasCullFace = gl.isEnabled(gl.CULL_FACE);

        const resources = this.glResources;
        if (!resources) return;

        if (this.bufferDirty) {
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
          gl.bufferData(gl.ARRAY_BUFFER, this.latest.vertices, gl.DYNAMIC_DRAW);
          gl.bindBuffer(gl.ARRAY_BUFFER, resources.overlayBuffer);
          gl.bufferData(
            gl.ARRAY_BUFFER,
            this.latest.overlayVertices,
            gl.DYNAMIC_DRAW,
          );
          this.bufferDirty = false;
        }

        if (this.latest.overlayPointCount <= 0) return;

        gl.useProgram(resources.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, resources.overlayBuffer);

        const stride = 21 * 4;

        gl.enableVertexAttribArray(resources.aPositionHigh);
        gl.vertexAttribPointer(
          resources.aPositionHigh,
          3,
          gl.FLOAT,
          false,
          stride,
          0,
        );

        gl.enableVertexAttribArray(resources.aPositionLow);
        gl.vertexAttribPointer(
          resources.aPositionLow,
          3,
          gl.FLOAT,
          false,
          stride,
          3 * 4,
        );

        gl.enableVertexAttribArray(resources.aOtherHigh);
        gl.vertexAttribPointer(
          resources.aOtherHigh,
          3,
          gl.FLOAT,
          false,
          stride,
          6 * 4,
        );

        gl.enableVertexAttribArray(resources.aOtherLow);
        gl.vertexAttribPointer(
          resources.aOtherLow,
          3,
          gl.FLOAT,
          false,
          stride,
          9 * 4,
        );

        gl.enableVertexAttribArray(resources.aSide);
        gl.vertexAttribPointer(
          resources.aSide,
          1,
          gl.FLOAT,
          false,
          stride,
          12 * 4,
        );

        gl.enableVertexAttribArray(resources.aAlong);
        gl.vertexAttribPointer(
          resources.aAlong,
          1,
          gl.FLOAT,
          false,
          stride,
          13 * 4,
        );

        gl.enableVertexAttribArray(resources.aColor);
        gl.vertexAttribPointer(
          resources.aColor,
          4,
          gl.FLOAT,
          false,
          stride,
          14 * 4,
        );

        gl.enableVertexAttribArray(resources.aData);
        gl.vertexAttribPointer(
          resources.aData,
          2,
          gl.FLOAT,
          false,
          stride,
          18 * 4,
        );
        gl.enableVertexAttribArray(resources.aWidth);
        gl.vertexAttribPointer(
          resources.aWidth,
          1,
          gl.FLOAT,
          false,
          stride,
          20 * 4,
        );

        gl.uniformMatrix4fv(
          resources.uMatrix,
          false,
          resolveProjectionMatrix({
            map: this.map,
            originMercator: this.originMercator,
            facadeTransitionProgress: this.facadeTransitionProgress,
            facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
            matrix,
          }),
        );

        // Henrik: Use fixed pixel sizes for dashes so they look consistent regardless of zoom.
        gl.uniform2f(
          resources.uDashParams,
          mapRenderingConstants.dashSizePx,
          mapRenderingConstants.gapSizePx,
        );
        gl.uniform1f(
          resources.uPixelsPerMercator,
          Math.pow(2, this.map.getZoom()) * 512,
        );

        const canvas = this.map.getCanvas();
        gl.uniform2f(resources.uResolution, canvas.width, canvas.height);

        // Henrik: the shader expects device pixels; keep config in CSS px so retina looks consistent.
        const dpr =
          typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
        const zoomScale = getLineWidthZoomScale(this.map.getZoom());
        gl.uniform1f(
          resources.uLineWidth,
          mapRenderingConstants.lineWidthPx * dpr * zoomScale,
        );

        gl.enable(gl.BLEND);

        // Preserve coverage alpha; multiplying source alpha twice makes drafts fade out.
        gl.blendFuncSeparate(
          gl.SRC_ALPHA,
          gl.ONE_MINUS_SRC_ALPHA,
          gl.ONE,
          gl.ONE_MINUS_SRC_ALPHA,
        );
        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.CULL_FACE);

        gl.drawArrays(gl.TRIANGLES, 0, this.latest.overlayPointCount);

        // Reset for Mapbox subsequent layers.
        if (wasDepthTest) gl.enable(gl.DEPTH_TEST);
        if (!wasBlend) gl.disable(gl.BLEND);
        if (wasCullFace) gl.enable(gl.CULL_FACE);
      },
      onRemove: (_mapInstance, gl) => {
        const resources = this.glResources;
        if (!resources) return;

        gl.deleteBuffer(resources.buffer);
        gl.deleteBuffer(resources.overlayBuffer);
        gl.deleteProgram(resources.program);
        this.glResources = undefined;
      },
    };
  }

  /**
   * Attach the custom layers to the map and begin listening to state updates.
   */
  attach(): void {
    const map = this.map;
    this.ensureLayerOrder();

    if (!this.detachStyleListener) {
      const handler = () => {
        // On style reload, Mapbox can drop custom layers; keep ours inserted below points.
        this.ensureLayerOrder();
      };

      map.on('styledata', handler);
      this.detachStyleListener = () => map.off('styledata', handler);
    }

    // Track zoom so right-angle indicators maintain constant screen size
    this.zoom$.next(map.getZoom());
    if (!this.detachZoomListener) {
      const onZoom = () => this.zoom$.next(map.getZoom());
      map.on('zoom', onZoom);
      this.detachZoomListener = () => map.off('zoom', onZoom);
    }

    const shapeTranslation$ = this.events.getShapeTranslation$();
    const selections$ = this.events.getSelectionObservables();
    const hoverState$ = this.events.getHoverState$();
    const draftPreview$ = this.events.getDraftPreview$();
    const pointMove$ = this.events.getPointMove$();

    this.subscription.add(
      combineLatest([
        this.state.storeys$,
        this.state.draftShape$,
        this.state.selectedStoreyId$,
        this.state.location$,
        this.state.locationPinMode$,
        this.state.penMode$,
        this.state.otherBuildings$,
        this.state.isBuildingOutlineSelected$,
        this.state.hoveredBuildingId$,
        shapeTranslation$,
        selections$.storeyId$,
        selections$.shapeId$,
        selections$.pointIndexes$,
        selections$.draftShape$,
        hoverState$,
        draftPreview$,
        pointMove$,
        this.readonly$,
        this.zoom$,
        this.renderParams$,
        this.mode$,
        this.softenGuidesBelowSelected$,
      ]).subscribe(
        ([
          storeys,
          draftShape,
          selectedStoreyId,
          location,
          locationPinMode,
          penMode,
          otherBuildings,
          isBuildingOutlineSelected,
          _hoveredBuildingId,
          translation,
          selectionStoreyId,
          selectionShapeId,
          selectionPointIndexes,
          selectionDraftShape,
          hoverState,
          draftPreview,
          pointMove,
          isReadonly,
          zoom,
          renderParams,
          mode,
          softenGuidesBelowSelected,
        ]) => {
          const originMercator = mapboxgl.MercatorCoordinate.fromLngLat(
            location,
            0,
          );
          this.originMercator = originMercator;

          // Pin replaces every building overlay; keep meshes empty until zoomed in.
          if (locationPinMode) {
            this.latest = EMPTY_WIREFRAME;
            this.latestExtrusion = EMPTY_EXTRUSION;
            this.latestPoints = {
              vertices: new Float32Array(),
              vertexCount: 0,
            };
            this.silhouetteMass = new Float32Array();
            this.hoveredSilhouetteMass = new Float32Array();
            this.bufferDirty = true;
            this.extrusionBufferDirty = true;
            this.pointsBufferDirty = true;
            this.silhouetteBufferDirty = true;
            map.triggerRepaint();
            return;
          }

          const suppressPoints = isReadonly;

          const effectiveStoreys = translation
            ? applyShapeTranslationPreview(storeys, translation)
            : storeys;
          const polygonDragOrigin = resolvePolygonDragOrigin({
            storeys,
            pointMove,
            translation,
          });

          // Include crosshair when draft preview has a snapped coordinate
          const footprintTemplateDrafts = draftPreview?.templateShapes;
          const effectiveDraftShape = isReadonly ? undefined : draftShape;
          const crosshairCoordinate =
            !isReadonly && draftShape && draftPreview?.snappedCoordinate
              ? draftPreview.snappedCoordinate
              : undefined;

          const otherBuildingStoreys = otherBuildings.map(
            (building) => building.storeys,
          );
          const otherBuildingsOpacity =
            this.state.hasBuildingSelection$.getValue() &&
            isClickAnywhereDrawMode(effectiveStoreys, effectiveDraftShape)
              ? CLICK_ANYWHERE_OTHER_BUILDINGS_OPACITY
              : 1;

          const selectedWireframe = buildWireframeVertices({
            storeys: effectiveStoreys,
            otherBuildingStoreys: [],
            draftShape: effectiveDraftShape ?? undefined,
            templateDraftShapes: isReadonly
              ? undefined
              : footprintTemplateDrafts,
            selectedStoreyId: selectedStoreyId ?? undefined,
            origin: location,
            mode,
            originMercator,
            crosshairCoordinate,
            zoom:
              suppressPoints || footprintTemplateDrafts?.length
                ? undefined
                : zoom,
            softenGuidesBelowSelected,
            penMode,
            polygonDragOrigin,
            closeDraftShape: Boolean(footprintTemplateDrafts?.length),
          });
          const selectedExtrusion = buildExtrusionVertices({
            storeys: effectiveStoreys,
            otherBuildingStoreys: [],
            selectedStoreyId: selectedStoreyId ?? undefined,
            origin: location,
            mode,
            originMercator,
            renderParams: renderParams as ExtrusionRenderParams,
          });
          const sibling = this.getCachedSiblingGeometries({
            otherBuildings,
            otherBuildingStoreys,
            indexStoreys: storeys,
            selectedStoreyId: selectedStoreyId ?? undefined,
            origin: location,
            mode,
            originMercator,
            renderParams: renderParams as ExtrusionRenderParams,
            otherBuildingsOpacity,
          });
          const wireframe = concatWireframeGeometries(
            {
              vertices: selectedWireframe.vertices,
              pointCount: selectedWireframe.vertexCount,
              storeySlices: selectedWireframe.storeySlices,
              pieceSlices: selectedWireframe.pieceSlices,
              cutoutVerticalSlices: selectedWireframe.cutoutVerticalSlices,
              overlayVertices: selectedWireframe.overlayVertices,
              overlayPointCount: selectedWireframe.overlayVertexCount,
            },
            sibling.wireframe,
          );
          const extrusion = concatExtrusionGeometries(
            selectedExtrusion,
            sibling.extrusion,
          );

          const selections: MapSelections = {
            storeyId: isReadonly
              ? undefined
              : (selectionStoreyId ?? selectedStoreyId ?? undefined),
            shapeId: isReadonly ? undefined : (selectionShapeId ?? undefined),
            pointIndexes: isReadonly
              ? undefined
              : (selectionPointIndexes ?? undefined),
            draftShape: !isReadonly && Boolean(selectionDraftShape),
          };

          const points =
            suppressPoints || Boolean(footprintTemplateDrafts?.length)
              ? { vertices: new Float32Array(), vertexCount: 0 }
              : buildPointVertices({
                  features: this.buildPointFeatures({
                    storeys: effectiveStoreys,
                    draftShape: effectiveDraftShape ?? undefined,
                    selections,
                    hoverState: isReadonly ? undefined : hoverState,
                    origin: location,
                  }),
                  originMercator,
                  penMode,
                });

          this.latest = wireframe;
          this.originMercator = originMercator;
          this.bufferDirty = true;

          this.latestExtrusion = extrusion;
          this.extrusionBufferDirty = true;

          this.latestPoints = points;
          this.pointsBufferDirty = true;

          // Silhouette masks only need each building's volume, nothing else.
          const toLocalPosition = (
            lng: number,
            lat: number,
            altitude: number,
          ): [number, number, number] => {
            const mercator = mapboxgl.MercatorCoordinate.fromLngLat(
              { lng, lat },
              altitude,
            );
            return [
              mercator.x - originMercator.x,
              mercator.y - originMercator.y,
              mercator.z - originMercator.z,
            ];
          };
          this.silhouetteMass = isBuildingOutlineSelected
            ? buildBuildingMassVertices({
                storeys: effectiveStoreys,
                origin: location,
                project: toLocalPosition,
              })
            : new Float32Array();
          this.updateHoveredSilhouette();
          this.silhouetteBufferDirty = true;

          map.triggerRepaint();
        },
      ),
    );
    // Hover changes only rebuild the small silhouette mask, not the whole scene.
    this.subscription.add(
      this.state.hoveredStoreyId$.subscribe(() =>
        this.updateHoveredSilhouette(),
      ),
    );
  }

  private updateHoveredSilhouette(): void {
    const location = this.state.location$.getValue();
    const hoveredStorey = this.state.hasBuildingSelection$.getValue()
      ? getHoveredStoreyForOutline(
          this.state.storeys$.getValue(),
          this.state.hoveredStoreyId$.getValue(),
          this.state.selectedStoreyId$.getValue(),
        )
      : undefined;
    this.hoveredStoreyOutlineActive = Boolean(hoveredStorey);
    const hoveredBuildingId = this.state.hoveredBuildingId$.getValue();
    const storeys = hoveredStorey
      ? [hoveredStorey]
      : this.state.otherBuildings$
          .getValue()
          .find((building) => building.id === hoveredBuildingId)?.storeys;
    const origin = mapboxgl.MercatorCoordinate.fromLngLat(location, 0);
    this.hoveredSilhouetteMass = this.state.locationPinMode$.getValue()
      ? new Float32Array()
      : buildBuildingMassVertices({
          storeys,
          origin: location,
          includeMezzanines: Boolean(hoveredStorey),
          project: (lng, lat, altitude) => {
            const point = mapboxgl.MercatorCoordinate.fromLngLat(
              { lng, lat },
              altitude,
            );
            return [point.x - origin.x, point.y - origin.y, point.z - origin.z];
          },
        });
    this.silhouetteBufferDirty = true;
    this.map.triggerRepaint();
  }

  /**
   * Stop listening and remove the layers.
   */
  destroy(): void {
    this.subscription.unsubscribe();
    this.detachStyleListener?.();
    this.detachStyleListener = undefined;
    this.detachZoomListener?.();
    this.detachZoomListener = undefined;
    this.readonly$.complete();
    this.zoom$.complete();

    if (this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID);
    }
    if (this.map.getLayer(FACADE_SKY_LAYER_ID)) {
      this.map.removeLayer(FACADE_SKY_LAYER_ID);
    }
    if (this.map.getLayer(MAP_THREEJS_LAYER_IDS.pointDragGuides)) {
      this.map.removeLayer(MAP_THREEJS_LAYER_IDS.pointDragGuides);
    }
    if (this.map.getLayer(EXTRUSION_LAYER_ID)) {
      this.map.removeLayer(EXTRUSION_LAYER_ID);
    }
    if (this.map.getLayer(POINTS_LAYER_ID)) {
      this.map.removeLayer(POINTS_LAYER_ID);
    }
    if (this.map.getLayer(SILHOUETTE_LAYER_ID)) {
      this.map.removeLayer(SILHOUETTE_LAYER_ID);
    }
  }

  /**
   * Sibling extrusion + wireframe, reused across shape-drag frames.
   * Selected-building geometry is rebuilt every frame; siblings are static.
   * `indexStoreys` is the selected stack with shapes stripped so
   * single-storey view still resolves the matching sibling floor.
   */
  private getCachedSiblingGeometries(options: {
    otherBuildings: Building[];
    otherBuildingStoreys: Storey[][];
    indexStoreys: Storey[];
    selectedStoreyId: string | undefined;
    origin: Coordinate;
    mode: ThreeJsMode;
    originMercator: mapboxgl.MercatorCoordinate;
    renderParams: ExtrusionRenderParams;
    otherBuildingsOpacity: number;
  }): { wireframe: WireframeLayerState; extrusion: ExtrusionLayerState } {
    if (!options.otherBuildingStoreys.length) {
      this.siblingGeometryCache = undefined;
      return { wireframe: EMPTY_WIREFRAME, extrusion: EMPTY_EXTRUSION };
    }

    const cache = this.siblingGeometryCache;
    if (
      cache &&
      cache.otherBuildings === options.otherBuildings &&
      cache.selectedStoreyId === options.selectedStoreyId &&
      cache.storeyCount === options.indexStoreys.length &&
      cache.mode === options.mode &&
      cache.opacity === options.otherBuildingsOpacity &&
      cache.lng === options.origin.lng &&
      cache.lat === options.origin.lat &&
      cache.renderParams === options.renderParams
    ) {
      return { wireframe: cache.wireframe, extrusion: cache.extrusion };
    }

    // Keep storey ids/order for selectedIndex; omit shapes so this pass emits
    // only sibling triangles.
    const indexStoreys = options.indexStoreys.map((storey) => ({
      ...storey,
      shapes: [],
    }));

    const packedWireframe = buildWireframeVertices({
      storeys: indexStoreys,
      otherBuildingStoreys: options.otherBuildingStoreys,
      selectedStoreyId: options.selectedStoreyId,
      origin: options.origin,
      mode: options.mode,
      originMercator: options.originMercator,
      otherBuildingsOpacity: options.otherBuildingsOpacity,
    });
    const extrusion = buildExtrusionVertices({
      storeys: indexStoreys,
      otherBuildingStoreys: options.otherBuildingStoreys,
      selectedStoreyId: options.selectedStoreyId,
      origin: options.origin,
      mode: options.mode,
      originMercator: options.originMercator,
      renderParams: options.renderParams,
      otherBuildingsOpacity: options.otherBuildingsOpacity,
    });
    const wireframe: WireframeLayerState = {
      vertices: packedWireframe.vertices,
      pointCount: packedWireframe.vertexCount,
      storeySlices: packedWireframe.storeySlices,
      pieceSlices: packedWireframe.pieceSlices,
      cutoutVerticalSlices: packedWireframe.cutoutVerticalSlices,
      overlayVertices: packedWireframe.overlayVertices,
      overlayPointCount: packedWireframe.overlayVertexCount,
    };
    this.siblingGeometryCache = {
      otherBuildings: options.otherBuildings,
      selectedStoreyId: options.selectedStoreyId,
      storeyCount: options.indexStoreys.length,
      mode: options.mode,
      opacity: options.otherBuildingsOpacity,
      lng: options.origin.lng,
      lat: options.origin.lat,
      renderParams: options.renderParams,
      wireframe,
      extrusion,
    };
    return { wireframe, extrusion };
  }

  private syncGlBuffer(gl: WebGLRenderingContext | null): void {
    const resources = this.glResources;
    if (!resources) return;
    if (!gl) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.latest.vertices, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.overlayBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.latest.overlayVertices,
      gl.DYNAMIC_DRAW,
    );
  }

  private syncExtrusionGlBuffer(gl: WebGLRenderingContext | null): void {
    const resources = this.extrusionGlResources;
    if (!resources) return;
    if (!gl) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.latestExtrusion.vertices,
      gl.DYNAMIC_DRAW,
    );
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.wallBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.latestExtrusion.wallVertices,
      gl.DYNAMIC_DRAW,
    );
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.roofBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.latestExtrusion.roofVertices,
      gl.DYNAMIC_DRAW,
    );
  }

  private syncPointsGlBuffer(gl: WebGLRenderingContext | null): void {
    const resources = this.pointsGlResources;
    if (!resources) return;
    if (!gl) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.latestPoints.vertices, gl.DYNAMIC_DRAW);
  }

  private syncSilhouetteGlBuffer(gl: WebGLRenderingContext | null): void {
    const resources = this.silhouetteGlResources;
    if (!resources || !gl) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.selectedMassBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.silhouetteMass, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, resources.hoveredMassBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.hoveredSilhouetteMass, gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    this.silhouetteBufferDirty = false;
  }

  /**
   * Insert missing custom layers and restack them under the GeoJSON points layer.
   */
  private ensureLayerOrder(): void {
    const map = this.map;
    // Skip getStyle/moveLayer while Standard re-merges on zoom; restack only if a custom layer dropped.
    if (areMapLayersPresent(map, MAP_THREEJS_CUSTOM_LAYER_IDS)) {
      this.hideLayersDuringMeasureIsolation();
      return;
    }

    const beforeId = map.getLayer(MapLayerIds.Points)
      ? MapLayerIds.Points
      : undefined;

    if (!map.getLayer(FACADE_SKY_LAYER_ID)) {
      map.addLayer(this.facadeSkyLayer, beforeId);
    }
    if (!map.getLayer(MAP_THREEJS_LAYER_IDS.pointDragGuides)) {
      map.addLayer(this.pointDragGuidesLayer, beforeId);
    }
    if (!map.getLayer(EXTRUSION_LAYER_ID)) {
      // Add extrusion first so the wireframe can render on top of it.
      map.addLayer(this.extrusionLayer, beforeId);
    }
    if (!map.getLayer(LAYER_ID)) {
      map.addLayer(this.layer, beforeId);
    }
    if (!map.getLayer(SILHOUETTE_LAYER_ID)) {
      // After the wireframe so the outline is never drawn over by geometry.
      map.addLayer(this.silhouetteLayer, beforeId);
    }
    if (!map.getLayer(POINTS_LAYER_ID)) {
      map.addLayer(this.pointsLayer, beforeId);
    }

    stackLayersBefore(map, MAP_THREEJS_LAYER_ORDER_STACK, beforeId);
    this.hideLayersDuringMeasureIsolation();
  }

  /**
   * Hide the custom building overlays so they cannot cover the PDF scale line.
   */
  private hideLayersDuringMeasureIsolation(): void {
    if (!this.measureIsolationActive) return;
    for (const layerId of Object.values(MAP_THREEJS_LAYER_IDS)) {
      try {
        setLayerVisibilityIfChanged(this.map, layerId, false);
      } catch {
        // Layer may be missing during a style swap.
      }
    }
  }

  private buildPointFeatures({
    storeys,
    draftShape,
    selections,
    hoverState,
    origin,
  }: {
    storeys: Parameters<typeof getMapStoreyPointsFromStoreys>[0];
    draftShape: Parameters<typeof getMapStoreyPointsFromStoreys>[3];
    selections: Parameters<typeof getMapStoreyPointsFromStoreys>[1];
    hoverState: MapHoverState | undefined;
    origin: Parameters<typeof getMapStoreyPointsFromStoreys>[4];
  }): MapStoreyPointFeature[] {
    const draftActive = Boolean(draftShape?.points?.length);

    if (!draftActive) {
      const geo = getMapStoreyPointsFromStoreys(
        storeys,
        selections,
        hoverState,
        undefined,
        origin,
        { includeInsertCandidate: true },
      );
      return geo.data.features;
    }

    const geo = getMapStoreyPointsFromStoreys(
      storeys,
      selections,
      hoverState,
      draftShape,
      origin,
    );
    return geo.data.features;
  }

  /**
   * Toggle read-only rendering to suppress edit affordances.
   * @param isReadonly When true, hide selection/hover overlays
   */
  setReadonly(isReadonly: boolean): void {
    if (this.isReadonly === isReadonly) return;
    this.isReadonly = isReadonly;
    this.readonly$.next(isReadonly);
  }

  /**
   * Patch extrusion render parameters and trigger a repaint.
   * @param patch Partial {@link ExtrusionRenderParams} to merge into current params
   */
  setExtrusionParams(patch: Partial<ExtrusionRenderParams>): void {
    this.renderParams$.next({ ...this.renderParams$.getValue(), ...patch });
    this.map.triggerRepaint();
  }

  /**
   * Switch which storeys the overlay draws ({@link ThreeJsMode}).
   */
  setMode(mode: ThreeJsMode): void {
    if (this.mode$.getValue() === mode) return;
    this.mode$.next(mode);
    this.map.triggerRepaint();
  }

  /** Render Nodon geometry at an exact 90° pitch above Mapbox's 85° cap. */
  setFacadeMode(active: boolean): void {
    if (this.facadeModeActive === active) return;
    this.facadeModeActive = active;
    this.map.triggerRepaint();
  }

  /** Animate between Mapbox's capped projection and the exact facade projection. */
  setFacadeTransitionProgress(progress: number): void {
    const clampedProgress = Math.min(1, Math.max(0, progress));
    if (this.facadeTransitionProgress === clampedProgress) return;
    this.facadeTransitionProgress = clampedProgress;
    this.map.triggerRepaint();
  }

  /** Project an overlay anchor with the same animated facade matrix as the model. */
  projectFacadeCoordinate(
    coordinate: Coordinate,
    elevationMeters: number,
  ): Point2D | undefined {
    if (!this.facadeModeActive || this.facadeTransitionProgress <= 0) {
      return undefined;
    }
    const transform = (
      this.map as unknown as {
        transform?: {
          width?: number;
          height?: number;
          customLayerMatrix?: () => readonly number[];
        };
      }
    ).transform;
    const sourceMatrix = transform?.customLayerMatrix?.();
    if (!sourceMatrix || sourceMatrix.length < 16) return undefined;
    const canvas = this.map.getCanvas();
    const width = transform?.width ?? canvas.clientWidth;
    const height = transform?.height ?? canvas.clientHeight;
    const projection = resolveProjectionMatrix({
      map: this.map,
      originMercator: this.originMercator,
      facadeTransitionProgress: this.facadeTransitionProgress,
      facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
      matrix: sourceMatrix,
    });
    const mercator = mapboxgl.MercatorCoordinate.fromLngLat(
      coordinate,
      elevationMeters,
    );
    const projected = projectLocalPointToScreen(
      projection,
      [
        mercator.x - this.originMercator.x,
        mercator.y - this.originMercator.y,
        mercator.z - this.originMercator.z,
      ],
      { width, height },
    );
    return projected ? [projected[0], projected[1]] : undefined;
  }

  /** Build a local-metre pick ray from the exact projection used to render facade mode. */
  getFacadePickRay(screenPoint: Point2D): BuildingVolumeRay | undefined {
    if (!this.facadeModeActive || this.facadeTransitionProgress <= 0) {
      return undefined;
    }
    const transform = (
      this.map as unknown as {
        transform?: {
          width?: number;
          height?: number;
          customLayerMatrix?: () => readonly number[];
        };
      }
    ).transform;
    const sourceMatrix = transform?.customLayerMatrix?.();
    if (!sourceMatrix || sourceMatrix.length < 16) return undefined;
    const canvas = this.map.getCanvas();
    const width = transform?.width ?? canvas.clientWidth;
    const height = transform?.height ?? canvas.clientHeight;
    const projection = resolveProjectionMatrix({
      map: this.map,
      originMercator: this.originMercator,
      facadeTransitionProgress: this.facadeTransitionProgress,
      facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
      matrix: sourceMatrix,
    });
    const localRay = unprojectScreenPointToLocalRay(projection, screenPoint, {
      width,
      height,
    });
    if (!localRay) return undefined;
    const mercatorUnitsPerMeter =
      this.originMercator.meterInMercatorCoordinateUnits();
    if (!(mercatorUnitsPerMeter > 0)) return undefined;
    return {
      origin: [
        localRay.origin[0] / mercatorUnitsPerMeter,
        localRay.origin[1] / mercatorUnitsPerMeter,
        localRay.origin[2] / mercatorUnitsPerMeter,
      ],
      direction: localRay.direction,
    };
  }

  /** Offset facade geometry and its ground line vertically in CSS pixels. */
  setFacadeVerticalOffsetPixels(offsetPixels: number): void {
    if (this.facadeVerticalOffsetPixels === offsetPixels) return;
    this.facadeVerticalOffsetPixels = offsetPixels;
    this.map.triggerRepaint();
  }

  /**
   * Soften wireframe guides for storeys below the selection (2D only).
   */
  setSoftenGuidesBelowSelected(enabled: boolean): void {
    if (this.softenGuidesBelowSelected$.getValue() === enabled) return;
    this.softenGuidesBelowSelected$.next(enabled);
    this.map.triggerRepaint();
  }

  /**
   * Hide solid fills for storeys below {@link elevationMeters} (PDF underlay).
   * Pass `undefined` to show all fills again.
   */
  setHideFillsBelowElevationMeters(elevationMeters: number | undefined): void {
    if (this.hideFillsBelowElevationMeters === elevationMeters) return;
    this.hideFillsBelowElevationMeters = elevationMeters;
    this.map.triggerRepaint();
  }

  /**
   * Hide building overlays while the user draws the PDF scale line.
   */
  setMeasureIsolation(active: boolean): void {
    if (this.measureIsolationActive === active) {
      this.hideLayersDuringMeasureIsolation();
      return;
    }
    this.measureIsolationActive = active;
    this.hideLayersDuringMeasureIsolation();
    this.map.triggerRepaint();
  }
}
